/**
 * dualsense-protocol.js — byte-level HID protocol for Sony PlayStation
 * controllers over WebHID: DualSense (0x0ce6), DualSense Edge (0x0df2),
 * DualShock 4 v1 / v2 (0x05c4 / 0x09cc).
 *
 * Every offset and flag in this file is copied from a reference
 * implementation, and tests/node/dualsense-protocol.test.mjs re-derives the
 * output layout from the reference STRUCT DEFINITIONS (field name + size, in
 * declaration order) instead of trusting the numbers written here:
 *
 *   - Linux kernel drivers/hid/hid-playstation.c (Sony's own driver): output
 *     report layout, LED / lightbar / rumble flags, Bluetooth framing
 *     (seq_tag, tag 0x10, CRC-32 seeded with 0xA2), DualShock 4 layouts,
 *     battery decoding, the LED "light out" setup that must precede colours.
 *   - SDL src/joystick/hidapi/SDL_hidapi_ps5.c (what Steam uses): trigger
 *     effect blocks at 10 (right) / 21 (left), 11 bytes each, enabled by bits
 *     0x04 (right) / 0x08 (left) of byte 0.
 *   - nondebug/dualsense dualsense-explorer.html (WebHID reference by the
 *     WebHID spec editor): transport detection from HID collections, reading
 *     feature report 0x05 to leave the compact Bluetooth mode, the trigger
 *     status bytes 41 (R2) / 42 (L2) in the input report.
 *   - Nielk1's TriggerEffectGenerator (via dualsense-ts 6.15.38): the
 *     official effect encodings 0x05 Off / 0x21 Feedback / 0x25 Weapon /
 *     0x26 Vibration — the only modes that update the trigger status nibble,
 *     so the controller can be seen acknowledging them.
 *
 * Why this file exists: until 2026-09-05 the tool built these reports from
 * memory inside the blade. Adaptive-trigger blocks were written at bytes
 * 12 / 22 with the enable bits in byte 1 (which there means «mic LED» and
 * «power save»), so the feature never worked once in four months; lightbar
 * channels were shifted by one byte; over Bluetooth the compact report was
 * dropped and the USB report id was written to a Bluetooth link. One layout
 * from memory, four independent defects, zero errors raised.
 *
 * Node-testable: `module.exports` when present, otherwise `window.DualSenseProtocol`.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module && module.exports) module.exports = api;
    if (root && typeof root === 'object') root.DualSenseProtocol = api;
})(typeof window !== 'undefined' ? window : (typeof self !== 'undefined' ? self : this), function () {
    'use strict';

    var VENDOR_SONY = 0x054c;

    // productId → model. `family` picks the report layouts; `features` tells the
    // UI which output panels exist on this hardware.
    var MODELS = {
        0x05c4: { kind: 'ds4-v1',          family: 'ds4',       label: 'DualShock 4 (v1)', touchpad: { w: 1920, h: 942 },  features: { adaptiveTriggers: false, playerLeds: false, mute: false } },
        0x09cc: { kind: 'ds4-v2',          family: 'ds4',       label: 'DualShock 4 (v2)', touchpad: { w: 1920, h: 942 },  features: { adaptiveTriggers: false, playerLeds: false, mute: false } },
        0x0ce6: { kind: 'dualsense',       family: 'dualsense', label: 'DualSense',        touchpad: { w: 1920, h: 1080 }, features: { adaptiveTriggers: true,  playerLeds: true,  mute: true  } },
        0x0df2: { kind: 'dualsense-edge',  family: 'dualsense', label: 'DualSense Edge',   touchpad: { w: 1920, h: 1080 }, features: { adaptiveTriggers: true,  playerLeds: true,  mute: true  } }
    };

    // ─── Report ids and sizes (payload sizes are WITHOUT the report id, the
    //     way WebHID hands them over) ─────────────────────────────────────────
    var DS = {
        INPUT_USB: 0x01, INPUT_USB_SIZE: 63,
        INPUT_BT: 0x31,  INPUT_BT_SIZE: 77,
        INPUT_BT_SIMPLE: 0x01, INPUT_BT_SIMPLE_SIZE: 9,
        OUTPUT_USB: 0x02, OUTPUT_USB_SIZE: 47,
        OUTPUT_BT: 0x31,  OUTPUT_BT_SIZE: 77,
        FEATURE_CALIBRATION: 0x05,
        // struct dualsense_output_report_common (47 bytes)
        COMMON_SIZE: 47,
        O_VALID_FLAG0: 0, O_VALID_FLAG1: 1, O_MOTOR_RIGHT: 2, O_MOTOR_LEFT: 3,
        O_MUTE_BUTTON_LED: 8, O_POWER_SAVE: 9,
        O_TRIGGER_RIGHT: 10, O_TRIGGER_LEFT: 21, TRIGGER_BLOCK_SIZE: 11,
        O_VALID_FLAG2: 38, O_LIGHTBAR_SETUP: 41, O_LED_BRIGHTNESS: 42, O_PLAYER_LEDS: 43,
        O_LIGHTBAR_RED: 44, O_LIGHTBAR_GREEN: 45, O_LIGHTBAR_BLUE: 46,
        // valid_flag0
        FLAG0_COMPATIBLE_VIBRATION: 0x01, FLAG0_HAPTICS_SELECT: 0x02,
        FLAG0_RIGHT_TRIGGER: 0x04, FLAG0_LEFT_TRIGGER: 0x08,
        // valid_flag1
        FLAG1_MIC_MUTE_LED: 0x01, FLAG1_POWER_SAVE: 0x02, FLAG1_LIGHTBAR: 0x04,
        FLAG1_RELEASE_LEDS: 0x08, FLAG1_PLAYER_INDICATOR: 0x10,
        // valid_flag2
        FLAG2_LIGHTBAR_SETUP: 0x02, FLAG2_COMPATIBLE_VIBRATION2: 0x04,
        LIGHTBAR_SETUP_LIGHT_OUT: 0x02,
        // Bluetooth output framing (struct dualsense_output_report_bt)
        BT_TAG: 0x10, BT_COMMON_OFFSET: 2,
        // struct dualsense_input_report (offsets relative to the common block)
        I_LX: 0, I_LY: 1, I_RX: 2, I_RY: 3, I_L2: 4, I_R2: 5, I_SEQ: 6,
        I_BUTTONS: 7, I_TOUCH: 32, I_TRIGGER_STATUS_R2: 41, I_TRIGGER_STATUS_L2: 42, I_STATUS: 52,
        // compact Bluetooth report 0x01 (PS5SimpleStatePacket_t)
        S_BUTTONS: 4, S_L2: 7, S_R2: 8
    };

    var DS4 = {
        INPUT_USB: 0x01, INPUT_USB_SIZE: 63,
        INPUT_BT: 0x11,  INPUT_BT_SIZE: 77,
        INPUT_BT_MINIMAL: 0x01, INPUT_BT_MINIMAL_SIZE: 9,
        OUTPUT_USB: 0x05, OUTPUT_USB_SIZE: 31,
        OUTPUT_BT: 0x11,  OUTPUT_BT_SIZE: 77,
        FEATURE_CALIBRATION: 0x02,
        // struct dualshock4_output_report_common (10 bytes)
        COMMON_SIZE: 10,
        O_VALID_FLAG0: 0, O_VALID_FLAG1: 1, O_MOTOR_RIGHT: 3, O_MOTOR_LEFT: 4,
        O_LIGHTBAR_RED: 5, O_LIGHTBAR_GREEN: 6, O_LIGHTBAR_BLUE: 7, O_BLINK_ON: 8, O_BLINK_OFF: 9,
        FLAG0_MOTOR: 0x01, FLAG0_LED: 0x02, FLAG0_LED_BLINK: 0x04,
        // Bluetooth output framing (struct dualshock4_output_report_bt)
        HWCTL_HID: 0x80, HWCTL_CRC32: 0x40, BT_COMMON_OFFSET: 2,
        // struct dualshock4_input_report_common
        I_LX: 0, I_LY: 1, I_RX: 2, I_RY: 3, I_BUTTONS: 4, I_L2: 7, I_R2: 8,
        I_STATUS: 29, I_NUM_TOUCH_REPORTS: 32, I_TOUCH_REPORT: 33, TOUCH_REPORT_SIZE: 9,
        BT_INPUT_COMMON_OFFSET: 2
    };

    var CRC32_SEED = 0xA2;   // PS_OUTPUT_CRC32_SEED — the hidp header byte is part of the CRC

    // ─── CRC-32 (zlib polynomial, reflected) ───────────────────────────────
    var _crcTable = null;
    function crcTable() {
        if (_crcTable) return _crcTable;
        var t = new Uint32Array(256);
        for (var i = 0; i < 256; i++) {
            var c = i;
            for (var j = 0; j < 8; j++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
            t[i] = c >>> 0;
        }
        _crcTable = t;
        return t;
    }
    function crc32(bytes) {
        var t = crcTable();
        var crc = 0xFFFFFFFF;
        for (var i = 0; i < bytes.length; i++) crc = t[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
        return (crc ^ 0xFFFFFFFF) >>> 0;
    }
    // Sign a Bluetooth output payload in place: CRC over [0xA2, reportId,
    // payload[0..len-5]] written little-endian into the last 4 bytes.
    function signBluetooth(reportId, payload) {
        var body = new Uint8Array(payload.length - 4 + 2);
        body[0] = CRC32_SEED;
        body[1] = reportId & 0xff;
        body.set(payload.subarray(0, payload.length - 4), 2);
        var crc = crc32(body);
        var n = payload.length;
        payload[n - 4] = crc & 0xff;
        payload[n - 3] = (crc >>> 8) & 0xff;
        payload[n - 2] = (crc >>> 16) & 0xff;
        payload[n - 1] = (crc >>> 24) & 0xff;
        return crc;
    }

    // ─── Device identity ───────────────────────────────────────────────────
    function filters() {
        return Object.keys(MODELS).map(function (pid) {
            return { vendorId: VENDOR_SONY, productId: Number(pid) };
        });
    }
    function model(productId) {
        return MODELS[productId] || null;
    }

    // WebHID does not say whether the device speaks over USB or Bluetooth, but
    // the two interfaces publish different report descriptors: the Bluetooth
    // collection carries output report 0x31 (DualSense) / 0x11 (DualShock 4),
    // the USB one carries 0x02 / 0x05. Deciding from the descriptor works
    // BEFORE the first input report — over Bluetooth the first reports are the
    // compact 0x01, which looks exactly like USB if you only look at the id.
    function transportFromCollections(collections, family) {
        var L = family === 'ds4' ? DS4 : DS;
        var sawUsb = false, sawBt = false;
        var list = collections || [];
        for (var i = 0; i < list.length; i++) {
            var outs = (list[i] && list[i].outputReports) || [];
            for (var j = 0; j < outs.length; j++) {
                var id = outs[j] && outs[j].reportId;
                if (id === L.OUTPUT_BT) sawBt = true;
                if (id === L.OUTPUT_USB) sawUsb = true;
            }
        }
        if (sawBt && !sawUsb) return 'bt';
        if (sawUsb && !sawBt) return 'usb';
        return 'unknown';
    }

    // Reading ANY feature report over Bluetooth makes the controller leave the
    // compact 0x01 mode and start sending the full 0x31 / 0x11 report. Both the
    // kernel and SDL read the calibration report; so does the WebHID explorer.
    function enhancedModeFeatureReportId(family) {
        return family === 'ds4' ? DS4.FEATURE_CALIBRATION : DS.FEATURE_CALIBRATION;
    }

    // ─── Input parsing ─────────────────────────────────────────────────────
    function u8(dv, i) { return dv.getUint8(i); }
    function axis(v) { return (v - 128) / 128; }

    function decodeButtons(b0, b1, b2, hasMute) {
        var hat = b0 & 0x0f;
        return {
            hat: hat,                              // 0..7 clockwise from N, 8 = neutral
            square:   !!(b0 & 0x10), cross: !!(b0 & 0x20), circle: !!(b0 & 0x40), triangle: !!(b0 & 0x80),
            l1: !!(b1 & 0x01), r1: !!(b1 & 0x02), l2: !!(b1 & 0x04), r2: !!(b1 & 0x08),
            create: !!(b1 & 0x10), options: !!(b1 & 0x20), l3: !!(b1 & 0x40), r3: !!(b1 & 0x80),
            ps: !!(b2 & 0x01), touchpad: !!(b2 & 0x02),
            // On the DualShock 4 bits 2..7 of this byte are a counter, not buttons.
            mute: hasMute ? !!(b2 & 0x04) : false
        };
    }

    // struct dualsense_touch_point / dualshock4_touch_point (4 bytes):
    //   contact (bit 7 = inactive, bits 0..6 = id), x_lo, x_hi:4 | y_lo:4, y_hi
    function decodeTouchPoint(dv, off) {
        var contact = u8(dv, off);
        var xy1 = u8(dv, off + 2);
        return {
            id: contact & 0x7f,
            active: !(contact & 0x80),
            x: u8(dv, off + 1) | ((xy1 & 0x0f) << 8),
            y: (xy1 >> 4) | (u8(dv, off + 3) << 4)
        };
    }

    // DualSense status[0]: low nibble = battery data (10% steps), high nibble =
    // charging status (hid-playstation.c dualsense_parse_report).
    function decodeDualSenseBattery(status0) {
        var data = status0 & 0x0f, charging = (status0 >> 4) & 0x0f;
        switch (charging) {
            case 0x0: return { level: Math.min(data * 10 + 5, 100), status: 'discharging' };
            case 0x1: return { level: Math.min(data * 10 + 5, 100), status: 'charging' };
            case 0x2: return { level: 100, status: 'full' };
            case 0xa: case 0xb: return { level: 0, status: 'error' };   // voltage / temperature out of range
            default:  return { level: 0, status: 'error' };            // 0xf charging error
        }
    }
    // DualShock 4 status[0]: low nibble = battery data, bit 4 = cable state;
    // on cable a value of 11 means full (DS4_BATTERY_STATUS_FULL).
    function decodeDualShock4Battery(status0) {
        var data = status0 & 0x0f, cable = !!(status0 & 0x10);
        if (cable && data >= 11) return { level: 100, status: 'full' };
        return { level: Math.min(data * 10 + 5, 100), status: cable ? 'charging' : 'discharging' };
    }

    function parseDualSense(reportId, dv) {
        var n = dv.byteLength;
        var mode, base;
        if (reportId === DS.INPUT_USB && n >= DS.INPUT_USB_SIZE) { mode = 'usb'; base = 0; }
        else if (reportId === DS.INPUT_BT && n >= DS.INPUT_BT_SIZE) { mode = 'bt'; base = 1; }   // 1-byte BT prefix
        else if (reportId === DS.INPUT_BT_SIMPLE && n >= DS.INPUT_BT_SIMPLE_SIZE && n < DS.INPUT_USB_SIZE) {
            // Compact Bluetooth report: sticks, buttons, then the triggers.
            return {
                ok: true, mode: 'bt-simple', family: 'dualsense',
                lx: axis(u8(dv, 0)), ly: -axis(u8(dv, 1)), rx: axis(u8(dv, 2)), ry: -axis(u8(dv, 3)),
                l2: u8(dv, DS.S_L2) / 255, r2: u8(dv, DS.S_R2) / 255,
                buttons: decodeButtons(u8(dv, DS.S_BUTTONS), u8(dv, DS.S_BUTTONS + 1), u8(dv, DS.S_BUTTONS + 2), false),
                touches: [], battery: null, triggerStatus: null
            };
        } else return null;

        var b = DS.I_BUTTONS + base;
        var touches = [];
        for (var i = 0; i < 2; i++) touches.push(decodeTouchPoint(dv, base + DS.I_TOUCH + i * 4));
        var r2s = u8(dv, base + DS.I_TRIGGER_STATUS_R2), l2s = u8(dv, base + DS.I_TRIGGER_STATUS_L2);
        return {
            ok: true, mode: mode, family: 'dualsense',
            lx: axis(u8(dv, base + DS.I_LX)), ly: -axis(u8(dv, base + DS.I_LY)),
            rx: axis(u8(dv, base + DS.I_RX)), ry: -axis(u8(dv, base + DS.I_RY)),
            l2: u8(dv, base + DS.I_L2) / 255, r2: u8(dv, base + DS.I_R2) / 255,
            seq: u8(dv, base + DS.I_SEQ),
            buttons: decodeButtons(u8(dv, b), u8(dv, b + 1), u8(dv, b + 2), true),
            touches: touches,
            battery: decodeDualSenseBattery(u8(dv, base + DS.I_STATUS)),
            // Bit 4 = an effect is engaged, low nibble = effect state. Only the
            // official effect modes (0x21/0x25/0x26/0x05) drive these bytes —
            // which is what makes an applied effect observable from the host.
            triggerStatus: {
                l2: { raw: l2s, active: !!(l2s & 0x10), state: l2s & 0x0f },
                r2: { raw: r2s, active: !!(r2s & 0x10), state: r2s & 0x0f }
            }
        };
    }

    function parseDualShock4(reportId, dv) {
        var n = dv.byteLength;
        var mode, base;
        if (reportId === DS4.INPUT_USB && n >= DS4.INPUT_USB_SIZE) { mode = 'usb'; base = 0; }
        else if (reportId === DS4.INPUT_BT && n >= DS4.INPUT_BT_SIZE) { mode = 'bt'; base = DS4.BT_INPUT_COMMON_OFFSET; }
        else if (reportId === DS4.INPUT_BT_MINIMAL && n >= DS4.INPUT_BT_MINIMAL_SIZE && n < DS4.INPUT_USB_SIZE) {
            // The minimal report is the first 9 bytes of the full one.
            return {
                ok: true, mode: 'bt-simple', family: 'ds4',
                lx: axis(u8(dv, 0)), ly: -axis(u8(dv, 1)), rx: axis(u8(dv, 2)), ry: -axis(u8(dv, 3)),
                l2: u8(dv, DS4.I_L2) / 255, r2: u8(dv, DS4.I_R2) / 255,
                buttons: decodeButtons(u8(dv, DS4.I_BUTTONS), u8(dv, DS4.I_BUTTONS + 1), u8(dv, DS4.I_BUTTONS + 2), false),
                touches: [], battery: null, triggerStatus: null
            };
        } else return null;

        var b = base + DS4.I_BUTTONS;
        var touches = [];
        var numReports = u8(dv, base + DS4.I_NUM_TOUCH_REPORTS);
        if (numReports > 0) {
            // Newest touch report first: [timestamp][point0 ×4][point1 ×4]
            var t0 = base + DS4.I_TOUCH_REPORT + 1;
            for (var i = 0; i < 2; i++) touches.push(decodeTouchPoint(dv, t0 + i * 4));
        }
        return {
            ok: true, mode: mode, family: 'ds4',
            lx: axis(u8(dv, base + DS4.I_LX)), ly: -axis(u8(dv, base + DS4.I_LY)),
            rx: axis(u8(dv, base + DS4.I_RX)), ry: -axis(u8(dv, base + DS4.I_RY)),
            l2: u8(dv, base + DS4.I_L2) / 255, r2: u8(dv, base + DS4.I_R2) / 255,
            seq: u8(dv, b + 2) >> 2,
            buttons: decodeButtons(u8(dv, b), u8(dv, b + 1), u8(dv, b + 2), false),
            touches: touches,
            battery: decodeDualShock4Battery(u8(dv, base + DS4.I_STATUS)),
            triggerStatus: null
        };
    }

    function parseInput(family, reportId, dv) {
        if (!dv || typeof dv.getUint8 !== 'function') return null;
        return family === 'ds4' ? parseDualShock4(reportId, dv) : parseDualSense(reportId, dv);
    }

    // ─── Trigger effects (11-byte block, official encodings) ───────────────
    var TRIGGER_MODE = { OFF: 0x05, FEEDBACK: 0x21, WEAPON: 0x25, VIBRATION: 0x26 };

    function clampInt(v, lo, hi) { v = Math.round(Number(v) || 0); return v < lo ? lo : (v > hi ? hi : v); }

    // Ten trigger zones (0..9); each zone carries a 3-bit force value.
    function zoneMasks(fromZone, value3) {
        var active = 0, packed = 0;
        for (var i = fromZone; i <= 9; i++) {
            active |= (1 << i);
            packed = (packed | (value3 << (3 * i))) >>> 0;
        }
        return { active: active, packed: packed >>> 0 };
    }

    /**
     * spec: { mode:'off'|'feedback'|'weapon'|'vibration',
     *         position 0..9 (feedback/vibration), start 2..7 + end start+1..8 (weapon),
     *         force 0..8 (0 = off), frequency 1..255 Hz (vibration) }
     */
    function triggerEffect(spec) {
        var block = new Uint8Array(DS.TRIGGER_BLOCK_SIZE);
        spec = spec || {};
        var mode = spec.mode || 'off';
        var force = clampInt(spec.force == null ? 8 : spec.force, 0, 8);
        if (mode === 'off' || force === 0) { block[0] = TRIGGER_MODE.OFF; return block; }

        if (mode === 'feedback') {
            var z = zoneMasks(clampInt(spec.position, 0, 9), (force - 1) & 0x07);
            block[0] = TRIGGER_MODE.FEEDBACK;
            block[1] = z.active & 0xff; block[2] = (z.active >> 8) & 0xff;
            block[3] = z.packed & 0xff; block[4] = (z.packed >>> 8) & 0xff;
            block[5] = (z.packed >>> 16) & 0xff; block[6] = (z.packed >>> 24) & 0xff;
            return block;
        }
        if (mode === 'weapon') {
            var start = clampInt(spec.start == null ? 2 : spec.start, 2, 7);
            var end = clampInt(spec.end == null ? 8 : spec.end, start + 1, 8);
            var zones = (1 << start) | (1 << end);
            block[0] = TRIGGER_MODE.WEAPON;
            block[1] = zones & 0xff; block[2] = (zones >> 8) & 0xff;
            block[3] = force - 1;
            return block;
        }
        if (mode === 'vibration') {
            var v = zoneMasks(clampInt(spec.position, 0, 9), (force - 1) & 0x07);
            block[0] = TRIGGER_MODE.VIBRATION;
            block[1] = v.active & 0xff; block[2] = (v.active >> 8) & 0xff;
            block[3] = v.packed & 0xff; block[4] = (v.packed >>> 8) & 0xff;
            block[5] = (v.packed >>> 16) & 0xff; block[6] = (v.packed >>> 24) & 0xff;
            block[9] = clampInt(spec.frequency == null ? 30 : spec.frequency, 1, 255);
            return block;
        }
        block[0] = TRIGGER_MODE.OFF;
        return block;
    }

    // ─── Output reports ────────────────────────────────────────────────────
    function to255(v) { return clampInt((Number(v) || 0) * 255, 0, 255); }

    /**
     * DualSense common block. opts is sparse — only what you pass gets its
     * valid-flag set, everything else is left alone by the firmware:
     *   rumble: { weak 0..1, strong 0..1 }   weak → motor_right (small, high-frequency)
     *                                        strong → motor_left (large, low-frequency)
     *   triggerLeft / triggerRight: 11-byte block from triggerEffect()
     *   lightbar: { r, g, b }
     *   playerLeds: 0..31 bitmask (bit 0 = LED 1), playerLedBrightness 0 high / 1 medium / 2 low
     *   lightbarSetup: true → the kernel's "light out" setup (needed once after
     *                  connect so the boot animation stops and colours take)
     *   muteLed: 0 off / 1 on / 2 pulse
     */
    function buildDualSenseCommon(opts) {
        var c = new Uint8Array(DS.COMMON_SIZE);
        var f0 = 0, f1 = 0, f2 = 0;
        opts = opts || {};
        if (opts.rumble) {
            f0 |= DS.FLAG0_COMPATIBLE_VIBRATION | DS.FLAG0_HAPTICS_SELECT;
            c[DS.O_MOTOR_RIGHT] = to255(opts.rumble.weak);
            c[DS.O_MOTOR_LEFT] = to255(opts.rumble.strong);
        }
        if (opts.triggerRight) { f0 |= DS.FLAG0_RIGHT_TRIGGER; c.set(opts.triggerRight.subarray(0, DS.TRIGGER_BLOCK_SIZE), DS.O_TRIGGER_RIGHT); }
        if (opts.triggerLeft)  { f0 |= DS.FLAG0_LEFT_TRIGGER;  c.set(opts.triggerLeft.subarray(0, DS.TRIGGER_BLOCK_SIZE), DS.O_TRIGGER_LEFT); }
        if (opts.muteLed != null) { f1 |= DS.FLAG1_MIC_MUTE_LED; c[DS.O_MUTE_BUTTON_LED] = clampInt(opts.muteLed, 0, 2); }
        if (opts.lightbar) {
            f1 |= DS.FLAG1_LIGHTBAR;
            c[DS.O_LIGHTBAR_RED] = clampInt(opts.lightbar.r, 0, 255);
            c[DS.O_LIGHTBAR_GREEN] = clampInt(opts.lightbar.g, 0, 255);
            c[DS.O_LIGHTBAR_BLUE] = clampInt(opts.lightbar.b, 0, 255);
        }
        if (opts.playerLeds != null) {
            f1 |= DS.FLAG1_PLAYER_INDICATOR;
            c[DS.O_PLAYER_LEDS] = opts.playerLeds & 0x1f;
            c[DS.O_LED_BRIGHTNESS] = clampInt(opts.playerLedBrightness || 0, 0, 2);
        }
        if (opts.lightbarSetup) {
            f2 |= DS.FLAG2_LIGHTBAR_SETUP;
            c[DS.O_LIGHTBAR_SETUP] = DS.LIGHTBAR_SETUP_LIGHT_OUT;
        }
        c[DS.O_VALID_FLAG0] = f0; c[DS.O_VALID_FLAG1] = f1; c[DS.O_VALID_FLAG2] = f2;
        return c;
    }

    /**
     * DualShock 4 common block. The kernel always sends rumble and lightbar
     * together (third-party pads cancel one when the other arrives alone), so
     * callers pass BOTH current values every time.
     *   rumble: { weak, strong }  weak → motor_right, strong → motor_left
     *   lightbar: { r, g, b }
     */
    function buildDualShock4Common(opts) {
        var c = new Uint8Array(DS4.COMMON_SIZE);
        var f0 = 0;
        opts = opts || {};
        if (opts.rumble) {
            f0 |= DS4.FLAG0_MOTOR;
            c[DS4.O_MOTOR_RIGHT] = to255(opts.rumble.weak);
            c[DS4.O_MOTOR_LEFT] = to255(opts.rumble.strong);
        }
        if (opts.lightbar) {
            f0 |= DS4.FLAG0_LED;
            c[DS4.O_LIGHTBAR_RED] = clampInt(opts.lightbar.r, 0, 255);
            c[DS4.O_LIGHTBAR_GREEN] = clampInt(opts.lightbar.g, 0, 255);
            c[DS4.O_LIGHTBAR_BLUE] = clampInt(opts.lightbar.b, 0, 255);
        }
        c[DS4.O_VALID_FLAG0] = f0;
        return c;
    }

    /**
     * Frame a common block for the wire.
     *   family: 'dualsense' | 'ds4'; transport: 'usb' | 'bt'
     *   seq: caller-owned counter for Bluetooth DualSense (0..15, wraps)
     * Returns { reportId, data: Uint8Array } — pass straight to device.sendReport().
     */
    function buildOutput(family, transport, opts, seq) {
        var bt = transport === 'bt';
        if (family === 'ds4') {
            var common4 = buildDualShock4Common(opts);
            if (!bt) {
                var usb4 = new Uint8Array(DS4.OUTPUT_USB_SIZE);
                usb4.set(common4, 0);
                return { reportId: DS4.OUTPUT_USB, data: usb4 };
            }
            var out4 = new Uint8Array(DS4.OUTPUT_BT_SIZE);
            out4[0] = DS4.HWCTL_HID | DS4.HWCTL_CRC32;   // hw_control: "there is HID data and a CRC"
            out4[1] = 0x00;                              // audio_control
            out4.set(common4, DS4.BT_COMMON_OFFSET);
            signBluetooth(DS4.OUTPUT_BT, out4);
            return { reportId: DS4.OUTPUT_BT, data: out4 };
        }
        var common = buildDualSenseCommon(opts);
        if (!bt) return { reportId: DS.OUTPUT_USB, data: common };
        var out = new Uint8Array(DS.OUTPUT_BT_SIZE);
        out[0] = ((seq | 0) & 0x0f) << 4;   // seq_tag: sequence number in the high nibble, tag 0 in the low
        out[1] = DS.BT_TAG;                 // "Tag must be set. Exact meaning is unclear." — hid-playstation.c
        out.set(common, DS.BT_COMMON_OFFSET);
        signBluetooth(DS.OUTPUT_BT, out);
        return { reportId: DS.OUTPUT_BT, data: out };
    }

    return {
        VENDOR_SONY: VENDOR_SONY,
        MODELS: MODELS,
        DS: DS,
        DS4: DS4,
        TRIGGER_MODE: TRIGGER_MODE,
        CRC32_SEED: CRC32_SEED,
        filters: filters,
        model: model,
        transportFromCollections: transportFromCollections,
        enhancedModeFeatureReportId: enhancedModeFeatureReportId,
        parseInput: parseInput,
        triggerEffect: triggerEffect,
        buildDualSenseCommon: buildDualSenseCommon,
        buildDualShock4Common: buildDualShock4Common,
        buildOutput: buildOutput,
        crc32: crc32,
        signBluetooth: signBluetooth
    };
});
